import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePageMeta } from '../hooks/usePageMeta';

export default function EssentialPath() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAuthed = user && typeof user === 'object';
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [progress, setProgress] = useState(null); // {explored, total}
  const essential = data?.essential;

  usePageMeta({
    title: essential?.name ? `${essential.name} | OVERALL WELLBEING` : 'One of the Essentials | OVERALL WELLBEING',
    description: essential?.card || 'Explore one of the Essentials — the everyday habits that matter most.',
    path: `/essentials/${slug}`,
    noindex: status === 'notfound' || essential?.seo_index === false,
  });

  useEffect(() => {
    let active = true;
    setStatus('loading');
    (async () => {
      try {
        const { data } = await api.get(`/knowledge/essentials/${slug}/guides`);
        if (!active) return;
        setData(data);
        setStatus('ok');
      } catch (e) {
        if (!active) return;
        setStatus(e.response?.status === 404 ? 'notfound' : 'error');
      }
    })();
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!isAuthed || status !== 'ok' || !essential?.code) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/me/foundation-progress');
        if (!active) return;
        const f = (data.foundations || []).find((x) => x.code === essential.code);
        if (f) setProgress({ explored: f.explored, total: f.total });
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [isAuthed, status, essential?.code]);

  if (status === 'loading') {
    return (
      <div className="w-full flex items-center justify-center py-space-3xl" data-testid="essential-loading">
        <span className="inline-block w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
        <span className="ml-space-sm font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">Opening your path…</span>
      </div>
    );
  }

  if (status === 'notfound' || status === 'error') {
    return (
      <div className="w-full px-margin-mobile lg:px-margin py-space-3xl" data-testid="essential-notfound">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-headline-md text-headline-md uppercase text-outline tracking-tight mb-space-sm">Essential not found</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg">We couldn’t find that Essential. Head back to Longevity to choose one of the Essentials.</p>
          <Link to="/longevity" className="inline-flex items-center gap-space-xs h-12 px-space-xl rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider hover:bg-primary transition-all">
            <Icon name="arrow_back" className="text-[18px]" /> Back to Longevity
          </Link>
        </div>
      </div>
    );
  }

  const { topics } = data;
  const count = topics.length;

  return (
    <div className="flex flex-col w-full" data-testid="essential-path">
      {/* Breadcrumbs */}
      <section className="w-full px-margin-mobile lg:px-margin pt-space-xl pb-space-lg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-space-md">
          <div className="flex items-center flex-wrap gap-space-xs text-on-surface-variant font-label-md text-label-md uppercase tracking-wider">
            <Link to="/longevity" className="hover:text-surface-tint transition-colors">THE ESSENTIALS</Link>
            <span className="text-surface-container-highest">/</span>
            <span className="text-outline font-extrabold" data-testid="essential-name">{essential.name}</span>
          </div>
          <Link to="/longevity" className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold hover:text-primary transition-colors">
            <Icon name="arrow_back" className="text-[18px]" /> All Essentials
          </Link>
        </div>
      </section>

      {/* HERO */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl bg-surface relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[440px] h-[440px] rounded-full bg-primary-container/50 blur-[120px] pointer-events-none -z-10" />
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-space-sm mb-space-md">
            <span className="w-2 h-2 rounded-full bg-surface-tint shadow-[0_0_8px_rgba(240,40,223,0.7)]" />
            <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold">ESSENTIAL {String(essential.order).padStart(2, '0')}</span>
            <span className="text-surface-container-highest">/</span>
            <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">GUIDED PATH</span>
          </div>
          <h1 data-testid="essential-title" className="font-headline-lg text-headline-lg lg:text-display-hero lg:font-display-hero uppercase text-outline tracking-tight leading-tight lg:leading-none mb-space-lg">
            {essential.name}
          </h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-3xl leading-relaxed mb-space-xl" data-testid="essential-intro">
            {essential.intro}
          </p>
          <div className="w-full p-space-md bg-surface-container rounded-lg flex flex-wrap items-center gap-space-lg">
            <div className="flex items-center gap-space-sm"><Icon name="star" className="text-[16px] text-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">Essential {String(essential.order).padStart(2, '0')}</span></div>
            <span className="hidden sm:inline text-surface-container-highest">•</span>
            <div className="flex items-center gap-space-sm"><Icon name="menu_book" className="text-[16px] text-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">{count} Guide{count === 1 ? '' : 's'}</span></div>
            {progress && (
              <>
                <span className="hidden sm:inline text-surface-container-highest">•</span>
                <div className="flex items-center gap-space-sm" data-testid="essential-progress-marker"><Icon name="explore" className="text-[16px] text-surface-tint" /><span className="font-label-md text-label-md uppercase tracking-wider text-outline">Guides explored: {progress.explored} / {progress.total}</span></div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* GUIDES */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-space-xl pb-space-md border-b border-surface-container">
            <div>
              <span className="font-label-md text-label-md text-surface-tint uppercase tracking-widest font-bold block mb-space-xs">STEP 2 · CHOOSE A GUIDE</span>
              <h2 className="font-headline-md text-headline-md text-outline uppercase tracking-tight">Guides in this Essential</h2>
            </div>
            <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant mt-space-sm md:mt-0">{count} guide{count === 1 ? '' : 's'}</span>
          </div>

          {count > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-md" data-testid="essential-guides">
              {topics.map((t) => (
                <Link
                  key={t.canonical_id}
                  to={`/topics/${t.slug}`}
                  data-testid={`essential-guide-${t.slug}`}
                  className="p-space-lg bg-surface rounded-lg hover:bg-primary-container transition-colors group flex items-start justify-between shadow-sm relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-surface-tint opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="pr-space-sm">
                    <h4 className="font-title-md text-title-md text-outline font-bold group-hover:text-surface-tint transition-colors">{t.title}</h4>
                    <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">{t.domain}</p>
                  </div>
                  <Icon name="chevron_right" className="text-outline group-hover:text-surface-tint group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="font-body-md text-body-md text-on-surface-variant" data-testid="essential-guides-empty">
              This path doesn’t have any guides yet.
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-3xl bg-surface-container">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <span className="font-label-md text-label-md text-surface-tint uppercase tracking-widest font-bold mb-space-xs">KEEP EXPLORING</span>
          <h2 className="font-headline-lg text-headline-lg text-outline uppercase tracking-tight mb-space-md">One calm step at a time</h2>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl mb-space-2xl">Explore another Essential, or browse the full library of practical guides.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-space-md w-full sm:w-auto">
            <Link to="/longevity" className="w-full sm:w-auto px-space-xl h-12 rounded bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-widest font-bold shadow-[0_8px_24px_rgba(240,40,223,0.30)] hover:bg-primary transition-all flex items-center justify-center gap-space-xs">
              <Icon name="grid_view" className="text-[20px]" /> All the Essentials
            </Link>
            <Link to="/resources" className="w-full sm:w-auto px-space-xl h-12 rounded bg-outline text-surface font-label-md text-label-md uppercase tracking-widest font-bold hover:bg-on-surface transition-all flex items-center justify-center gap-space-xs">
              <Icon name="menu_book" className="text-[20px]" /> Browse the Library
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
